import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../configureStore";
import { IInstitution, updateInstitutionSteps } from "../../../redux/reducer/institution";
import { BusinessParams, ConfigStepBusiness, SignatureDocument } from "../../../utils/configuration";
import {
    Button,
    Col,
    Input,
    Row,
    Space,
    Switch,
    Typography,
    Card,
    Tabs,
    Alert,
    Divider,
    Select,
} from "antd";
import { useCallback, useState, CSSProperties } from "react";
import { SignatureDocumentModalForm } from "./SignatureDocumentModal";
import { LivenessConfigurationForm } from "./LivenessConfigurationForm";
import { Forms } from "./Transcript";
import { useMsal } from "@azure/msal-react";
import { AxiosResponse } from "axios";
import {
    InfoCircleOutlined,
    FileProtectOutlined,
    FormOutlined,
    EditOutlined,
    SaveOutlined,
    SettingOutlined,
    BankOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { TabPane } = Tabs;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '16px',
    },
    header: {
        marginBottom: '24px',
    },
    nameOverrideSection: {
        backgroundColor: '#fafafa',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
    },
    infoAlert: {
        marginBottom: '16px',
    },
    tabIcon: {
        marginRight: '8px',
    },
    settingCard: {
        marginBottom: '16px',
    },
};

interface AdminBusinessParams {
    stepIndex: number;
    step: ConfigStepBusiness;
}

const BusinessConfiguration = ({
    params,
    update,
    loading,
}: {
    params: BusinessParams;
    update: (p: Partial<BusinessParams>) => void;
    loading: boolean;
}) => (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
            message="Business Step Configuration"
            description="Configure how businesses are handled in this step. These settings control user permissions and signature behavior."
            type="info"
            showIcon
            closable
            icon={<InfoCircleOutlined />}
            style={styles.infoAlert}
        />

        <Card title={<Space><SettingOutlined />General Settings</Space>} style={styles.settingCard}>
            <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>Allow Business Addition</Text>
                        <Switch
                            checked={params.allowAddition}
                            loading={loading}
                            onChange={(checked) => update({ allowAddition: checked })}
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                        />
                        <Text type="secondary">
                            Allow applicants to add new businesses during this step
                        </Text>
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>Signature Mode</Text>
                        <Select
                            value={params.signatureMode}
                            onChange={(value) => update({ signatureMode: value })}
                            style={{ width: '100%' }}
                            options={[
                                {
                                    value: 'individual',
                                    label: 'Individual (per business)',
                                },
                                {
                                    value: 'shared',
                                    label: 'Shared (one for all)',
                                },
                            ]}
                        />
                        <Text type="secondary">
                            Individual: Separate signature request per business.
                            Shared: One signature covers all businesses.
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>

        <Card title={<Space><BankOutlined />Liveness Configuration</Space>} style={styles.settingCard}>
            <LivenessConfigurationForm
                beforeSignature={params.liveness?.beforeSignature}
                actionNeeded={params.liveness?.actionNeeded}
                displayText={params.liveness?.displayText}
                onUpdate={(partial) => update({
                    liveness: {
                        enabled: params.liveness?.enabled ?? false,
                        beforeSignature: params.liveness?.beforeSignature,
                        actionNeeded: params.liveness?.actionNeeded,
                        displayText: params.liveness?.displayText,
                        ...partial,
                    }
                })}
            />
            <Divider />
            <Row align="middle">
                <Col flex="auto">
                    <Text>Enable Liveness Check</Text>
                    <br />
                    <Text type="secondary">Require identity verification during the signature process</Text>
                </Col>
                <Col>
                    <Switch
                        checked={params.liveness?.enabled ?? false}
                        loading={loading}
                        onChange={(checked) => update({
                            liveness: {
                                enabled: checked,
                                beforeSignature: params.liveness?.beforeSignature,
                                actionNeeded: params.liveness?.actionNeeded,
                                displayText: params.liveness?.displayText,
                            }
                        })}
                        checkedChildren="Enabled"
                        unCheckedChildren="Disabled"
                    />
                </Col>
            </Row>
        </Card>
    </Space>
);

const AdminBusiness = ({
    stepIndex,
    step,
}: AdminBusinessParams) => {
    const { accounts: [account] } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const institution = useSelector<RootState, IInstitution | undefined>(
        ({ institution: { testInstitution } }) => testInstitution
    );
    const [nameOverride, setNameOverride] = useState<string | undefined>(step.nameOverride);
    const loading = useSelector<RootState, boolean>(
        ({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']
    );

    const update = useCallback((
        updated: Partial<BusinessParams>,
        onSuccess?: (res: AxiosResponse<IInstitution>) => void
    ) => {
        if (institution) {
            const copied = structuredClone(step);
            copied.params = {
                ...step.params,
                ...updated,
            };

            const services = institution.services.map((serve, idx) =>
                (stepIndex === idx ? copied : serve)
            );

            dispatch(updateInstitutionSteps({
                headers: {
                    Authorization: `Bearer ${account.idToken}`,
                },
                input: {
                    id: institution.id,
                    institutionId: institution.institutionId,
                    services,
                },
                message: { success: 'Successfully updated!', error: 'Failed to update step' },
                onSuccess,
            }, 'params'));
        }
    }, [step, stepIndex, institution, dispatch, account.idToken]);

    const handleSaveNameOverride = () => {
        if (nameOverride && institution) {
            const copied = structuredClone(step);
            copied.nameOverride = nameOverride;

            const services = institution.services.map((serve, idx) =>
                (stepIndex === idx ? copied : serve)
            );

            dispatch(updateInstitutionSteps({
                headers: {
                    Authorization: `Bearer ${account.idToken}`,
                },
                input: {
                    id: institution.id,
                    institutionId: institution.institutionId,
                    services,
                },
                message: { success: 'Step name updated successfully!', error: 'Failed to update step name' },
            }, 'params'));
        }
    };

    const updateDocument = useCallback((document: SignatureDocument) => {
        update({ document });
    }, [update]);

    return (
        <div style={styles.container}>
            <div style={styles.nameOverrideSection}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Step Display Name</Text>
                            <Input
                                value={nameOverride}
                                onChange={(e) => setNameOverride(e.target.value)}
                                placeholder="Enter custom step name (default: Business)"
                                prefix={<EditOutlined />}
                                size="large"
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            size="large"
                            loading={loading}
                            icon={<SaveOutlined />}
                            onClick={handleSaveNameOverride}
                            disabled={!nameOverride || nameOverride === step.nameOverride}
                        >
                            Update Name
                        </Button>
                    </Col>
                </Row>
            </div>

            <Tabs defaultActiveKey="configuration" size="large">
                <TabPane
                    tab={
                        <span>
                            <SettingOutlined style={styles.tabIcon} />
                            Configuration
                        </span>
                    }
                    key="configuration"
                >
                    <BusinessConfiguration
                        params={step.params}
                        update={update}
                        loading={loading}
                    />
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <FileProtectOutlined style={styles.tabIcon} />
                            Authorization Document
                        </span>
                    }
                    key="authorization"
                >
                    <Alert
                        message="Authorization Document"
                        description="Configure the signature document that will be used for business authorization. This document will be presented to users for signing."
                        type="info"
                        showIcon
                        closable
                        style={styles.infoAlert}
                    />
                    <SignatureDocumentModalForm
                        onFinish={updateDocument}
                        document={step.params.document}
                        enabled={true}
                    />
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <FormOutlined style={styles.tabIcon} />
                            Forms
                        </span>
                    }
                    key="forms"
                >
                    <Forms
                        useTenant={step.params.useTenant ?? false}
                        overrideTenantId={step.params.overrideTenantId}
                        forms={step.params.forms}
                        type="Business"
                        update={update as any}
                    />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default AdminBusiness;
